import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Grid, Link, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  DataGridPremium as DataGrid,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid-premium';
import { format } from 'date-fns';
import { Coupon, useCoupon_GetAllCouponsQuery } from 'queries';
import { ChevronRight, Icon } from 'ts-react-feather-icons';

import Spinner from 'common/spinner';
import HomeLayout from 'layouts/home-layout';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

import CreateCouponModal from './components/create-coupon-modal';

const useStyles = makeStyles(() => ({
  root: {
    // no border around the cells when focused
    '&.MuiDataGrid-root .MuiDataGrid-cell, &.MuiDataGrid-root .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
  },
  container: {
    padding: '5%',
    flexGrow: 1,
  },
  title: {
    fontWeight: 600,
  },
  routeIconContainer: {
    verticalAlign: 'middle',
  },
  link: {
    cursor: 'pointer',
  },
  description: {
    marginRight: '10px',
  },
}));

export default function Coupons() {
  const classes = useStyles();
  const history = useHistory();

  const [currentCoupon, setCurrentCoupon] = useState<Coupon | undefined>();
  const [showModal, setShowModal] = useState(false);

  const { data: couponsData, loading: couponsLoading } = useCoupon_GetAllCouponsQuery({
    onError: (e) => {
      showAlert({
        message: 'Error getting all coupons: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  if (couponsLoading) {
    return <Spinner />;
  }

  const columns: GridColDef[] = [
    {
      disableColumnMenu: true,
      field: 'description',
      flex: 3,
      headerName: 'Description',
      renderCell: (params: GridRenderCellParams) => {
        const coupon = params.row as Coupon;

        return (
          <>
            <Typography className={classes.description}>{coupon.description}</Typography>
            <Link
              className={classes.link}
              onClick={() => {
                setCurrentCoupon(coupon);
                setShowModal(true);
              }}
            >
              <Icon name="edit-2" size={16} color="#44D2F2" />
            </Link>
          </>
        );
      },
    },
    {
      disableColumnMenu: true,
      field: 'code',
      flex: 3,
      headerName: 'Code',
    },
    {
      disableColumnMenu: true,
      field: 'couponType',
      filterable: false,
      flex: 2,
      headerName: 'Type',
      renderCell: (params: GridRenderCellParams) => {
        const coupon = params.row as Coupon;

        return coupon.couponType === 'FLAT_FEE' ? 'Flat Fee' : '% Discount';
      },
    },
    {
      disableColumnMenu: true,
      field: 'amount',
      filterable: false,
      flex: 2,
      headerName: 'Amount',
      renderCell: (params: GridRenderCellParams) => {
        const coupon = params.row as Coupon;

        return coupon.couponType === 'FLAT_FEE'
          ? (coupon.amount / 100).toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD',
            })
          : `${coupon.amount}%`;
      },
    },
    {
      disableColumnMenu: true,
      field: 'allowedUses',
      filterable: false,
      flex: 2,
      headerName: 'Allowed Uses',
      renderCell: (params: GridRenderCellParams) => {
        const coupon = params.row as Coupon;

        return coupon.allowedUses && coupon.allowedUses > 0 ? coupon.allowedUses : 'premium';
      },
    },
    {
      disableColumnMenu: true,
      field: 'expirationDate',
      filterable: false,
      flex: 2,
      headerName: 'Expires',
      renderCell: (params: GridRenderCellParams) => {
        const coupon = params.row as Coupon;

        return coupon.expirationDate
          ? format(Date.parse(coupon.expirationDate.toString()), 'LLL d, y')
          : '';
      },
    },
  ];

  const rows = [];
  const coupons = couponsData ? couponsData.data : [];
  for (const coupon of coupons) {
    rows.push(coupon);
  }

  return (
    <HomeLayout showPortalName={false}>
      <>
        <CreateCouponModal
          currentCoupon={currentCoupon}
          setCurrentCoupon={setCurrentCoupon}
          showModal={showModal}
          setShowModal={setShowModal}
        />
        <div className={classes.container}>
          <div style={{ display: 'flex', verticalAlign: 'bottom' }}>
            <Typography className={classes.title} variant="h5" style={{ flex: 1 }}>
              <span
                onClick={() => {
                  history.push(`/home`);
                }}
              >
                <span className={classes.routeIconContainer}>
                  <img src="/home-icon.svg" alt="home-icon" />
                </span>
                <span className={classes.routeIconContainer}>
                  <ChevronRight />
                </span>
              </span>
              Manage Coupons
            </Typography>
            <div style={{ flex: 3 }}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                style={{ marginTop: '3px', padding: '2px 10px' }}
                onClick={() => {
                  setShowModal(true);
                }}
              >
                + Add a New Coupon
              </Button>
            </div>
          </div>
          <Grid container spacing={2}>
            <Grid item md={12} xs={12}>
              <div style={{ height: 600, width: '100%' }}>
                <DataGrid
                  className={classes.root}
                  columns={columns}
                  rows={rows}
                  disableColumnReorder
                  disableRowSelectionOnClick
                  autoHeight={true}
                  rowHeight={65}
                  pagination
                  initialState={{
                    pagination: { paginationModel: { pageSize: 100 } },
                  }}
                />
              </div>
            </Grid>
          </Grid>
        </div>
      </>
    </HomeLayout>
  );
}
